// =============================================================================
// ui/state/useDayBaseline.ts — "vs your average day".
// -----------------------------------------------------------------------------
// The Day Overview's Net P&L tile compares the day against the mean of every
// day you have traded, which needs the WHOLE BOOK — and no existing read serves
// it:
//
//   the facet universe   has dates but no P&L at all
//   the prop universe    is dollars-only by design, so it cannot answer the
//                        comparator in points, ticks or R
//   the range read       is the day itself
//
// So it reuses the METRICS projection unbounded. Eleven columns, one request per
// 1,000 trades, and it already carries `contract` / `qty` / `points` / `netPnl`
// / `riskPoints` / `rrRealized` — everything `tradeValue` needs in every unit.
//
// ── ⚠ FIRES ONLY WHEN THE TAB MOUNTS ──
//
// Same call as `usePropUniverse`: one surface needs the whole history, so a
// provider would make every user pay the read to open the Dashboard.
//
// ── 🚨 NULL IS "NOT KNOWN", AND THE TILE MUST RENDER THE SUB-LABEL EMPTY ──
//
// A failed or pending baseline must not fall back to 0. "avg day $0" is a real
// and quite specific claim — a breakeven trader — and printing it under a
// winning day would read as "you doubled your usual", which is the flattering
// direction and therefore the dangerous one.
// =============================================================================

import { useEffect, useState } from 'react';
import { loadTradeMetrics } from '../../io/sync/load';
import type { MetricsRow } from '../../selectors/population';
import { useSupabase } from './supabase';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface DayBaseline {
  /** ⚠ NULL while loading AND on failure. Never an empty array standing in. */
  rows: readonly MetricsRow[] | null;
  error: string | null;
}

export function useDayBaseline(): DayBaseline {
  const { sb } = useSupabase();
  const [rows, setRows] = useState<readonly MetricsRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await loadTradeMetrics(sb, {});
        if (!cancelled) setRows(r.rows);
      } catch (e) {
        if (cancelled) return;
        // ⚠ Stays NULL. The comparator sub-label simply does not render — the
        // rest of the tab is unaffected, because one missing comparison is not
        // worth failing a day's review over.
        setError(reportFailure('day baseline', e));
      }
    })();
    return () => { cancelled = true; };
  }, [sb]);

  return { rows, error };
}
